import { memo } from 'react';
import { Button } from '@/hen-ui/atomics/Button';
import type { SponsoredCardProps } from './types';

type SponsoredVisitButtonProps = Pick<
  SponsoredCardProps,
  'visitHref' | 'visitLabel' | 'onVisitClick' | 'sponsoredVisit'
> & {
  className?: string;
};

/**
 * Outbound "Visit Site" CTA for a sponsored card.
 *
 * `visitHref` is the destination signal and gates whether the CTA renders at
 * all (a whitespace-only href counts as absent). When present, the optional
 * `onVisitClick` handler owns the click: the CTA renders as a <button> with no
 * href for the browser to follow, so no orphaned tab can spawn alongside the
 * handler's own linkout window. Without a handler the href renders as a genuine
 * external link (`target='_blank'` + `noopener noreferrer` and, unless
 * `sponsoredVisit` is false, `sponsored`).
 */
export const SponsoredVisitButton = memo(function SponsoredVisitButton({
  visitHref,
  visitLabel = 'Visit Site',
  onVisitClick,
  sponsoredVisit = true,
  className,
}: SponsoredVisitButtonProps) {
  const resolvedVisitHref = visitHref?.trim();
  if (!resolvedVisitHref) return null;

  if (onVisitClick) {
    return (
      <Button
        type='button'
        variant='primaryFill'
        size='sm'
        onClick={onVisitClick}
        className={className}
      >
        {visitLabel}
      </Button>
    );
  }

  return (
    <Button
      variant='primaryFill'
      size='sm'
      href={resolvedVisitHref}
      target='_blank'
      rel={`noopener noreferrer${sponsoredVisit === false ? '' : ' sponsored'}`}
      className={className}
    >
      {visitLabel}
    </Button>
  );
});
